// Copyright (c) 2026 SoundDrift. All rights reserved.

import React from 'react';
import { useSearchParams } from 'react-router-dom';
import Lobby from '../HostView/Lobby.jsx';
import HowToPlay from '../HostView/HowToPlay.jsx';
import { SoundOffHost } from '../modes/SoundOff/SoundOffHost.jsx';
import { SoundOffPlayer } from '../modes/SoundOff/SoundOffPlayer.jsx';
import { AnsweringUI } from '../modes/SoundOff/AnsweringUI.jsx';
import { DuelReveal } from '../modes/SoundOff/DuelReveal.jsx';
import { NameThatNoiseHost } from '../modes/NameThatNoise/NameThatNoiseHost.jsx';
import { NameThatNoisePlayer } from '../modes/NameThatNoise/NameThatNoisePlayer.jsx';
import DriftHost from '../modes/Drift/DriftHost.jsx';
import DriftPlayer from '../modes/Drift/DriftPlayer.jsx';
import { GrandReveal } from '../modes/Drift/GrandReveal.jsx';
import { FoleyHost } from '../modes/Foley/FoleyHost.jsx';
import { FoleyPlayer } from '../modes/Foley/FoleyPlayer.jsx';
import { RadioDramaBuilder } from '../modes/Foley/RadioDramaBuilder.jsx';
import Join from '../PlayerView/Join.jsx';
import PhaseStatus from '../Common/PhaseStatus.jsx';
import Header from '../Common/Header.jsx';
import SoundPicker from '../Common/SoundPicker/SoundPicker.jsx';
import { WaveformVisualizer } from '../Common/WaveformVisualizer.jsx';
import '../../styles/theme.css';

const SAMPLE_CLIPS = [
  { id: 'sfx_heavy_wooden_door_slam_01', label: 'Heavy Wooden Door Slam', duration: 2.4, color: '#3ACFCF', peaks: [0.2, 0.5, 0.9, 0.8, 0.4, 0.1, 0.05, 0.02], category: 'Impact' },
  { id: 'sfx_laser_blaster_power_down_01', label: 'Laser Blaster Power Down', duration: 1.8, color: '#E0A458', peaks: [0.8, 0.9, 0.7, 0.5, 0.3, 0.2, 0.1, 0.05], category: 'Sci-Fi' },
  { id: 'sfx_duck_quack_01', label: 'Duck Quack Funny', duration: 1.2, color: '#8FC7A0', peaks: [0.3, 0.7, 0.8, 0.6, 0.2, 0.1], category: 'Animal' },
  { id: 'sfx_screaming_goat', label: 'Screaming Goat Echo', duration: 3.1, color: '#E4738A', peaks: [0.1, 0.4, 0.8, 0.9, 0.9, 0.7, 0.4, 0.2], category: 'Meme' },
  { id: 'sfx_metal_pipe_falling', label: 'Metal Pipe Clatter', duration: 2.8, color: '#9A8CF0', peaks: [0.9, 0.8, 0.7, 0.6, 0.5, 0.3, 0.2], category: 'Impact' },
  { id: 'sfx_creaky_wood_footsteps', label: 'Creaky Wood Footsteps', duration: 3.5, color: '#6FB2E4', peaks: [0.2, 0.5, 0.3, 0.6, 0.4, 0.7, 0.2], category: 'Foley' },
  { id: 'sfx_glass_shatter_bar_01', label: 'Glass Shatter Crash', duration: 1.6, color: '#D98E6A', peaks: [0.9, 0.95, 0.8, 0.5, 0.3, 0.1], category: 'Crash' },
  { id: 'sfx_rain_gentle_ambient_02', label: 'Rain Gentle Ambient', duration: 4.0, color: '#B9A2D9', peaks: [0.3, 0.4, 0.4, 0.45, 0.4, 0.35, 0.3], category: 'Ambience' },
];

const MOCK_PLAYERS = {
  p1: { id: 'p1', name: 'Alex M.', avatarClip: SAMPLE_CLIPS[0], ready: true, score: 3200 },
  p2: { id: 'p2', name: 'Sam K.', avatarClip: SAMPLE_CLIPS[1], ready: true, score: 2850 },
  p3: { id: 'p3', name: 'Jordan T.', avatarClip: SAMPLE_CLIPS[2], ready: true, score: 2400 },
  p4: { id: 'p4', name: 'Taylor R.', avatarClip: SAMPLE_CLIPS[3], ready: false, score: 1900 },
};

export default function GalleryView() {
  const [params] = useSearchParams();
  const screen = params.get('screen') || 'list';

  // 1. HOST LOBBY EMPTY
  if (screen === 'host_lobby_empty') {
    return (
      <div style={{ width: '100vw', height: '100vh', background: 'var(--sd-ground)', padding: '24px', boxSizing: 'border-box' }}>
        <Header isHost roomCode="SD89" round={1} maxRounds={3} />
        <Lobby roomCode="SD89" players={{}} minPlayers={3} maxPlayers={8} selectedMode="soundoff" />
      </div>
    );
  }

  // 2. HOST LOBBY 4 PLAYERS
  if (screen === 'host_lobby_4_players') {
    return (
      <div style={{ width: '100vw', height: '100vh', background: 'var(--sd-ground)', padding: '24px', boxSizing: 'border-box' }}>
        <Header isHost roomCode="SD89" round={1} maxRounds={3} />
        <Lobby roomCode="SD89" players={MOCK_PLAYERS} minPlayers={3} maxPlayers={8} selectedMode="soundoff" />
      </div>
    );
  }

  // 3. HOST HOW TO PLAY MODAL
  if (screen === 'host_how_to_play') {
    return (
      <div style={{ width: '100vw', height: '100vh', background: 'var(--sd-ground)', padding: '24px', boxSizing: 'border-box' }}>
        <HowToPlay isOpen={true} onClose={() => {}} mode="soundoff" />
      </div>
    );
  }

  // 4. HOST SOUND OFF AUTHORING
  if (screen === 'host_soundoff_authoring') {
    const room = {
      roomCode: 'SD89',
      currentPhase: 'authoring',
      phaseEndTime: Date.now() + 45000,
      players: MOCK_PLAYERS,
      waitingOn: ['p4'],
      modeState: {
        currentPhase: 'authoring',
        submittedPrompts: { p1: 'When the coffee kicks in', p2: 'Stepping on a lego at 3am', p3: 'Surprise birthday party gone wrong' }
      }
    };
    return (
      <div style={{ width: '100vw', height: '100vh', background: 'var(--sd-ground)', padding: '24px', boxSizing: 'border-box' }}>
        <Header isHost roomCode="SD89" round={1} maxRounds={3} timer={45} />
        <SoundOffHost room={room} />
      </div>
    );
  }

  // 5. HOST SOUND OFF ANSWERING
  if (screen === 'host_soundoff_answering') {
    const room = {
      roomCode: 'SD89',
      currentPhase: 'answering',
      phaseEndTime: Date.now() + 32000,
      players: MOCK_PLAYERS,
      waitingOn: ['p2', 'p4'],
      modeState: {
        currentPhase: 'answering',
        submissions: {
          p1: { promptId: 'prompt_1', clips: [SAMPLE_CLIPS[0], SAMPLE_CLIPS[1]] },
          p3: { promptId: 'prompt_3', clips: [SAMPLE_CLIPS[2], SAMPLE_CLIPS[3]] }
        }
      }
    };
    return (
      <div style={{ width: '100vw', height: '100vh', background: 'var(--sd-ground)', padding: '24px', boxSizing: 'border-box' }}>
        <Header isHost roomCode="SD89" round={1} maxRounds={3} timer={32} />
        <SoundOffHost room={room} />
      </div>
    );
  }

  // 6. HOST SOUND OFF DUEL REVEAL
  if (screen === 'host_soundoff_duel_reveal') {
    const duels = [
      {
        prompt: { text: 'When you drop your phone face down on the concrete sidewalk', authorId: 'p3', authorName: 'Jordan T.' },
        duelistA: { id: 'p1', name: 'Alex M.', avatarClip: SAMPLE_CLIPS[0], clips: [SAMPLE_CLIPS[0], SAMPLE_CLIPS[4]] },
        duelistB: { id: 'p2', name: 'Sam K.', avatarClip: SAMPLE_CLIPS[1], clips: [SAMPLE_CLIPS[1], SAMPLE_CLIPS[6]] },
      }
    ];
    return (
      <div style={{ width: '100vw', height: '100vh', background: 'var(--sd-ground)', padding: '24px', boxSizing: 'border-box' }}>
        <Header isHost roomCode="SD89" round={1} maxRounds={3} timer={20} />
        <DuelReveal
          duels={duels}
          currentDuelIndex={0}
          votes={{ p3: 'p1', p4: 'p2' }}
          phase="reveal"
          players={MOCK_PLAYERS}
        />
      </div>
    );
  }

  // 7. HOST NAME THAT NOISE BLUFFING
  if (screen === 'host_namethatnoise_bluffing') {
    const room = {
      roomCode: 'SD89',
      currentPhase: 'bluffing',
      phaseEndTime: Date.now() + 38000,
      players: MOCK_PLAYERS,
      waitingOn: ['p3'],
      modeState: {
        phase: 'bluffing',
        mysteryClip: SAMPLE_CLIPS[0],
        bluffs: {
          p1: 'Slamming a pirate treasure chest shut',
          p2: 'Castle dungeon door closing',
          p4: 'Vintage heavy refrigerator sealing'
        }
      }
    };
    return (
      <div style={{ width: '100vw', height: '100vh', background: 'var(--sd-ground)', padding: '24px', boxSizing: 'border-box' }}>
        <Header isHost roomCode="SD89" round={2} maxRounds={3} timer={38} />
        <NameThatNoiseHost room={room} />
      </div>
    );
  }

  // 8. HOST NAME THAT NOISE VOTING
  if (screen === 'host_namethatnoise_voting') {
    const room = {
      roomCode: 'SD89',
      currentPhase: 'voting',
      phaseEndTime: Date.now() + 18000,
      players: MOCK_PLAYERS,
      waitingOn: ['p1'],
      modeState: {
        phase: 'voting',
        mysteryClip: SAMPLE_CLIPS[0],
        options: [
          { id: 'opt_truth', text: 'Heavy Wooden Door Slam', isTruth: true },
          { id: 'opt_1', text: 'Slamming a pirate treasure chest shut', authorId: 'p1' },
          { id: 'opt_2', text: 'Castle dungeon door closing', authorId: 'p2' },
          { id: 'opt_4', text: 'Vintage heavy refrigerator sealing', authorId: 'p4' }
        ],
        votes: { p2: 'opt_truth', p3: 'opt_1', p4: 'opt_truth' }
      }
    };
    return (
      <div style={{ width: '100vw', height: '100vh', background: 'var(--sd-ground)', padding: '24px', boxSizing: 'border-box' }}>
        <Header isHost roomCode="SD89" round={2} maxRounds={3} timer={18} />
        <NameThatNoiseHost room={room} />
      </div>
    );
  }

  // 9. HOST NAME THAT NOISE RESULTS
  if (screen === 'host_namethatnoise_results') {
    const room = {
      roomCode: 'SD89',
      currentPhase: 'results',
      phaseEndTime: Date.now() + 8000,
      players: MOCK_PLAYERS,
      modeState: {
        phase: 'results',
        mysteryClip: SAMPLE_CLIPS[0],
        options: [
          { id: 'opt_truth', text: 'Heavy Wooden Door Slam', isTruth: true },
          { id: 'opt_1', text: 'Slamming a pirate treasure chest shut', authorId: 'p1' },
          { id: 'opt_2', text: 'Castle dungeon door closing', authorId: 'p2' },
          { id: 'opt_4', text: 'Vintage heavy refrigerator sealing', authorId: 'p4' }
        ],
        votes: { p1: 'opt_2', p2: 'opt_truth', p3: 'opt_1', p4: 'opt_truth' },
        roundResults: {
          mysteryClip: SAMPLE_CLIPS[0],
          scores: { p1: 500, p2: 1000, p3: 0, p4: 1000 }
        }
      }
    };
    return (
      <div style={{ width: '100vw', height: '100vh', background: 'var(--sd-ground)', padding: '24px', boxSizing: 'border-box' }}>
        <Header isHost roomCode="SD89" round={2} maxRounds={3} timer={8} />
        <NameThatNoiseHost room={room} />
      </div>
    );
  }

  // 10. HOST DRIFT GRAND REVEAL
  if (screen === 'host_drift_grand_reveal') {
    const chain = {
      initialPrompt: 'A spooky ghost haunting a house',
      authorName: 'Alex M.',
      entries: [
        { type: 'text', text: 'A spooky ghost haunting a house', authorName: 'Alex M.', authorId: 'p1' },
        { type: 'audio', clipId: 'sfx_screaming_goat', clipTitle: 'Screaming Goat Echo', authorName: 'Sam K.', authorId: 'p2' },
        { type: 'text', text: 'Goat yelling in the mountains', authorName: 'Jordan T.', authorId: 'p3' },
        { type: 'audio', clipId: 'sfx_duck_quack_01', clipTitle: 'Duck Quack Funny', authorName: 'Taylor R.', authorId: 'p4' },
      ]
    };
    return (
      <div style={{ width: '100vw', height: '100vh', background: 'var(--sd-ground)', padding: '24px', boxSizing: 'border-box' }}>
        <Header isHost roomCode="SD89" round={3} maxRounds={3} />
        <GrandReveal chain={chain} chainIndex={0} totalChains={4} isHost={true} />
      </div>
    );
  }

  // 11. HOST FOLEY DUEL PLAYBACK
  if (screen === 'host_foley_duel_playback') {
    const room = {
      roomCode: 'SD89',
      currentPhase: 'duel',
      phaseEndTime: Date.now() + 25000,
      players: MOCK_PLAYERS,
      modeState: {
        currentPhase: 'duel',
        prompt: { text: 'Bank heist escape gone wrong in the rainy alley', authorName: 'Jordan T.' },
        duels: [
          {
            duelistAId: 'p1',
            duelistBId: 'p2',
            promptText: 'Bank heist escape gone wrong in the rainy alley'
          }
        ],
        submissions: {
          p1: { clips: ['sfx_heavy_wooden_door_slam_01', 'sfx_screaming_goat', 'sfx_metal_pipe_falling'], gaps: [0.5, 1.2] },
          p2: { clips: ['sfx_glass_shatter_bar_01', 'sfx_laser_blaster_power_down_01', 'sfx_rain_gentle_ambient_02'], gaps: [0.8, 0.4] }
        }
      }
    };
    return (
      <div style={{ width: '100vw', height: '100vh', background: 'var(--sd-ground)', padding: '24px', boxSizing: 'border-box' }}>
        <Header isHost roomCode="SD89" round={1} maxRounds={3} timer={25} />
        <FoleyHost room={room} />
      </div>
    );
  }

  // 12. PLAYER JOIN VIEW (Mobile: 390x844)
  if (screen === 'player_join') {
    return (
      <div style={{ width: '390px', minHeight: '844px', background: 'var(--sd-ground)', padding: '20px', boxSizing: 'border-box', margin: '0 auto' }}>
        <Join initialRoomCode="SD89" initialPlayerName="Alex M." signatureClips={SAMPLE_CLIPS} />
      </div>
    );
  }

  // 13. PLAYER WAITING / PHASE STATUS
  if (screen === 'player_waiting_phase') {
    return (
      <div style={{ width: '390px', minHeight: '844px', background: 'var(--sd-ground)', padding: '20px', boxSizing: 'border-box', margin: '0 auto', display: 'flex', flexDirection: 'column' }}>
        <Header roomCode="SD89" isPlayer={true} playerName="Alex M." playerAvatar={SAMPLE_CLIPS[0]} />
        <PhaseStatus
          phase="answering"
          phaseEndTime={Date.now() + 28000}
          waitingOn={['p2', 'p4']}
          players={MOCK_PLAYERS}
          currentPlayerId="p1"
          mode="soundoff"
        />
      </div>
    );
  }

  // 14. PLAYER SOUND OFF ANSWERING CLIP PICKER
  if (screen === 'player_soundoff_picker') {
    return (
      <div style={{ width: '390px', minHeight: '844px', background: 'var(--sd-ground)', padding: '16px', boxSizing: 'border-box', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Header roomCode="SD89" isPlayer={true} playerName="Alex M." playerAvatar={SAMPLE_CLIPS[0]} timer={42} />
        <AnsweringUI
          assignedPrompt={{ text: 'When you drop your phone face down on concrete', authorName: 'Jordan T.' }}
          hand={SAMPLE_CLIPS}
        />
      </div>
    );
  }

  // 15. PLAYER NAME THAT NOISE BLUFFING
  if (screen === 'player_namethatnoise_bluffing') {
    return (
      <div style={{ width: '390px', minHeight: '844px', background: 'var(--sd-ground)', padding: '16px', boxSizing: 'border-box', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Header roomCode="SD89" isPlayer={true} playerName="Alex M." playerAvatar={SAMPLE_CLIPS[0]} timer={35} />
        <NameThatNoisePlayer
          phase="bluffing"
          modeState={{}}
          playerId="p1"
        />
      </div>
    );
  }

  // 16. PLAYER NAME THAT NOISE VOTING
  if (screen === 'player_namethatnoise_voting') {
    const modeState = {
      options: [
        { id: 'opt_truth', text: 'Heavy Wooden Door Slam', isTruth: true },
        { id: 'opt_1', text: 'Slamming a pirate treasure chest shut', authorId: 'p1' },
        { id: 'opt_2', text: 'Castle dungeon door closing', authorId: 'p2' },
        { id: 'opt_4', text: 'Vintage heavy refrigerator sealing', authorId: 'p4' }
      ]
    };
    return (
      <div style={{ width: '390px', minHeight: '844px', background: 'var(--sd-ground)', padding: '16px', boxSizing: 'border-box', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Header roomCode="SD89" isPlayer={true} playerName="Alex M." playerAvatar={SAMPLE_CLIPS[0]} timer={19} />
        <NameThatNoisePlayer
          phase="voting"
          modeState={modeState}
          playerId="p1"
        />
      </div>
    );
  }

  // 17. PLAYER NAME THAT NOISE RESULTS
  if (screen === 'player_namethatnoise_results') {
    const modeState = {
      roundResults: {
        mysteryClip: SAMPLE_CLIPS[0],
        roundScores: {
          p1: { pickedTruth: false, fooledCount: 2, fooledPoints: 1000, totalRoundScore: 1000 }
        }
      }
    };
    return (
      <div style={{ width: '390px', minHeight: '844px', background: 'var(--sd-ground)', padding: '16px', boxSizing: 'border-box', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Header roomCode="SD89" isPlayer={true} playerName="Alex M." playerAvatar={SAMPLE_CLIPS[0]} />
        <NameThatNoisePlayer
          phase="results"
          modeState={modeState}
          playerId="p1"
        />
      </div>
    );
  }

  // 18. PLAYER DRIFT AUDIO STEP
  if (screen === 'player_drift_audio_step') {
    const modeState = {
      chainLength: 4,
      chains: [
        [
          { type: 'text', text: 'A frantic scientist discovering time travel', authorName: 'Alex M.', authorId: 'p1' }
        ]
      ]
    };
    return (
      <div style={{ width: '390px', minHeight: '844px', background: 'var(--sd-ground)', padding: '16px', boxSizing: 'border-box', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Header roomCode="SD89" isPlayer={true} playerName="Sam K." playerAvatar={SAMPLE_CLIPS[1]} timer={38} />
        <DriftPlayer
          phase="step_1"
          modeState={modeState}
          playerId="p2"
        />
      </div>
    );
  }

  // 19. PLAYER FOLEY SCENE BUILDER
  if (screen === 'player_foley_builder') {
    return (
      <div style={{ width: '390px', minHeight: '844px', background: 'var(--sd-ground)', padding: '16px', boxSizing: 'border-box', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Header roomCode="SD89" isPlayer={true} playerName="Alex M." playerAvatar={SAMPLE_CLIPS[0]} timer={45} />
        <RadioDramaBuilder
          assignedPrompt={{ text: 'Late night break-in at the museum' }}
          hand={SAMPLE_CLIPS}
        />
      </div>
    );
  }

  // INDEX LIST
  return (
    <div style={{ padding: '32px', color: 'var(--sd-ink)', fontFamily: 'sans-serif' }}>
      <h1>SoundDrift Visual Screen Gallery</h1>
      <p>Select a screen to view:</p>
      <ul>
        <li><a href="/gallery?screen=host_lobby_empty">Host Lobby (Empty)</a></li>
        <li><a href="/gallery?screen=host_lobby_4_players">Host Lobby (4 Players)</a></li>
        <li><a href="/gallery?screen=host_how_to_play">Host How To Play Modal</a></li>
        <li><a href="/gallery?screen=host_soundoff_authoring">Host Sound Off Authoring</a></li>
        <li><a href="/gallery?screen=host_soundoff_answering">Host Sound Off Answering</a></li>
        <li><a href="/gallery?screen=host_soundoff_duel_reveal">Host Sound Off Duel Reveal</a></li>
        <li><a href="/gallery?screen=host_namethatnoise_bluffing">Host Name That Noise Bluffing</a></li>
        <li><a href="/gallery?screen=host_namethatnoise_voting">Host Name That Noise Voting</a></li>
        <li><a href="/gallery?screen=host_namethatnoise_results">Host Name That Noise Results</a></li>
        <li><a href="/gallery?screen=host_drift_grand_reveal">Host Drift Grand Reveal</a></li>
        <li><a href="/gallery?screen=host_foley_duel_playback">Host Foley Duel Playback</a></li>
        <li><a href="/gallery?screen=player_join">Player Join View</a></li>
        <li><a href="/gallery?screen=player_waiting_phase">Player Waiting Phase</a></li>
        <li><a href="/gallery?screen=player_soundoff_picker">Player Sound Off Picker</a></li>
        <li><a href="/gallery?screen=player_namethatnoise_bluffing">Player Name That Noise Bluffing</a></li>
        <li><a href="/gallery?screen=player_namethatnoise_voting">Player Name That Noise Voting</a></li>
        <li><a href="/gallery?screen=player_namethatnoise_results">Player Name That Noise Results</a></li>
        <li><a href="/gallery?screen=player_drift_audio_step">Player Drift Audio Step</a></li>
        <li><a href="/gallery?screen=player_foley_builder">Player Foley Builder</a></li>
      </ul>
    </div>
  );
}
